/**
 * Getting a package out of the door, and its tax invoice.
 *
 * The seller's side of the Meesho/Flipkart flow after accepting an order:
 * pack it and register it with the courier (which issues the GST invoice),
 * book the courier, print the label and manifest, and hand it over at pickup.
 * From there the courier's scans move it to shipped and delivered — the seller
 * never sets those.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ReceiptText } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge, Button, Card, CardBody, PageHeader, Spinner } from '@terra/ui';

import { api } from '../lib/api';

const formatPaise = (p: number) =>
  `₹${(p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

interface CourierRate {
  courier_company_id: number;
  courier_name: string;
  rate: number;
  etd?: string;
  estimated_delivery_days?: number;
}

/** Statuses at which the courier has not collected the parcel yet. */
const BEFORE_PICKUP = ['PENDING', 'AWB_ASSIGNED', 'PICKUP_SCHEDULED'];

function errorText(err: unknown): string | null {
  if (!err) return null;
  return err instanceof Error ? err.message : 'Something went wrong. Try again.';
}

const CHARGE_LABEL: Record<string, string> = {
  FORWARD_FREIGHT: 'Courier freight',
  COD_FEE: 'COD collection fee',
  RTO_FREIGHT: 'Return-to-origin freight',
  REVERSE_FREIGHT: 'Return pickup freight',
  WEIGHT_DISCREPANCY: 'Weight discrepancy',
  OTHER_ADJUSTMENT: 'Courier adjustment',
};

/**
 * What the courier charged for this package that the seller pays — an
 * estimate from the booking until Shiprocket's bill confirms the actual — and
 * how much of it has been deducted from their settlement so far.
 */
export function CourierCostsCard({ subOrderId }: { subOrderId: string }) {
  const q = useQuery({
    queryKey: ['vendor-courier-charges', subOrderId],
    queryFn: () => api.orders.vendorCourierCharges(subOrderId),
  });
  if (q.isLoading || !q.data) return null;
  const { sellerPaysCourier, customerDeliveryChargePaise, charges } = q.data;
  return (
    <Card className="detail-card overflow-hidden rounded-xl bg-white shadow-sm">
      <CardBody className="space-y-3 p-6">
        <h2 className="text-lg font-black text-slate-900">Courier costs</h2>
        <p className="text-sm text-slate-500">
          {sellerPaysCourier
            ? `You pay the courier for this package and keep the customer's delivery charge (${formatPaise(customerDeliveryChargePaise)}). Costs are deducted at settlement.`
            : 'Terra Shoppy books and pays the courier for delivery. You are charged only for a weight discrepancy, a parcel returned undelivered (RTO), or a return of a damaged, wrong or incomplete item — those appear here and are deducted at settlement.'}
        </p>
        {charges.length === 0 && (
          <p className="text-sm text-slate-500">No courier charges on this package.</p>
        )}
        {charges.length > 0 && (
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase text-slate-400">
              <tr>
                <th className="py-1">Charge</th>
                <th className="py-1 text-right">Amount</th>
                <th className="py-1 text-right">Deducted</th>
              </tr>
            </thead>
            <tbody>
              {charges.map((c) => (
                <tr key={c.id} className="border-t border-slate-100">
                  <td className="py-1.5">
                    {CHARGE_LABEL[c.type] ?? c.type}
                    {c.awbNumber && (
                      <span className="ml-1 font-mono text-xs text-slate-400">{c.awbNumber}</span>
                    )}
                  </td>
                  <td className="py-1.5 text-right">
                    {c.actualPaise !== null
                      ? formatPaise(c.actualPaise)
                      : c.estimatedPaise !== null
                        ? `${formatPaise(c.estimatedPaise)} (estimate)`
                        : 'Awaiting courier bill'}
                  </td>
                  <td className="py-1.5 text-right">{formatPaise(c.chargedToYouPaise)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}

export function FulfilmentPanel({ subOrder }: { subOrder: any }) {
  const qc = useQueryClient();
  const [weight, setWeight] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const shipment = subOrder.shipment as
    | {
        id: string;
        status: string;
        awbNumber: string | null;
        courierName: string | null;
        labelUrl: string | null;
        manifestUrl: string | null;
      }
    | null
    | undefined;
  const liveShipment = shipment && shipment.status !== 'CANCELLED' ? shipment : null;

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['vendor-order-detail', subOrder.id] });
    void qc.invalidateQueries({ queryKey: ['vendor-orders'] });
  };

  const create = useMutation({
    mutationFn: () => {
      const grams = Number(weight);
      return api.orders.createShipment(
        subOrder.id,
        grams > 0 ? { weightGrams: Math.round(grams) } : {},
      );
    },
    onSuccess: refresh,
  });
  // The platform assigns the courier; a seller books without choosing one.
  const book = useMutation({
    mutationFn: () => api.orders.bookCourier(liveShipment!.id),
    onSuccess: refresh,
  });
  const retry = useMutation({
    mutationFn: () => api.orders.retryPickup(liveShipment!.id),
    onSuccess: refresh,
  });
  const docs = useMutation({
    mutationFn: () => api.orders.downloadDocuments(liveShipment!.id),
    onSuccess: refresh,
  });
  const cancel = useMutation({
    mutationFn: () => api.orders.cancelShipment(liveShipment!.id, cancelReason.trim()),
    onSuccess: () => {
      setCancelling(false);
      setCancelReason('');
      refresh();
    },
  });

  const error = errorText(create.error ?? book.error ?? retry.error ?? docs.error ?? cancel.error);
  const labelUrl = docs.data?.labelUrl ?? liveShipment?.labelUrl ?? null;
  const manifestUrl = docs.data?.manifestUrl ?? liveShipment?.manifestUrl ?? null;
  const canShip = ['CONFIRMED', 'PACKED'].includes(subOrder.status);

  return (
    <Card className="detail-card overflow-hidden rounded-xl bg-white shadow-sm">
      <CardBody className="space-y-4 p-6">
        <h2 className="text-lg font-black text-slate-900">Ship this order</h2>

        {error && (
          <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        )}

        {subOrder.status === 'PLACED' && (
          <p className="text-sm text-slate-500">Accept the order first, then pack and ship it.</p>
        )}

        {canShip && !liveShipment && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Pack the items, then register the parcel with the courier. This issues the GST invoice
              and marks the order packed.
            </p>
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                Packed weight in grams (optional)
              </span>
              <input
                type="number"
                min={1}
                inputMode="numeric"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="Taken from product sizes if left blank"
                className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-3 text-sm focus:border-[#a8161b] focus:outline-none focus:ring-1 focus:ring-[#a8161b]"
              />
            </label>
            <Button className="w-full" onClick={() => create.mutate()} loading={create.isPending}>
              Pack &amp; create shipment
            </Button>
          </div>
        )}

        {liveShipment && (
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50/60 p-3 text-sm">
              <div>
                <p className="text-slate-600">
                  Courier:{' '}
                  <span className="font-bold">{liveShipment.courierName ?? 'Not booked'}</span>
                </p>
                <p className="text-slate-600">
                  AWB: <span className="font-mono font-bold">{liveShipment.awbNumber ?? '—'}</span>
                </p>
              </div>
              <Badge tone="info" className="text-xs font-bold">
                {liveShipment.status.replace(/_/g, ' ')}
              </Badge>
            </div>

            {liveShipment.status === 'PENDING' && (
              <div className="space-y-2">
                <Button className="w-full" onClick={() => book.mutate()} loading={book.isPending}>
                  Book courier
                </Button>
                <p className="text-xs text-slate-500">
                  Terra Shoppy assigns the courier — the one the customer was quoted at checkout, or
                  the best available if it no longer serves this route.
                </p>
              </div>
            )}

            {liveShipment.status === 'AWB_ASSIGNED' && (
              <div className="space-y-2">
                <p className="text-sm text-amber-700">
                  The courier is booked but the pickup could not be scheduled.
                </p>
                <Button className="w-full" onClick={() => retry.mutate()} loading={retry.isPending}>
                  Retry pickup
                </Button>
              </div>
            )}

            {liveShipment.awbNumber && (
              <div className="space-y-2">
                {labelUrl || manifestUrl ? (
                  <div className="grid grid-cols-2 gap-2">
                    {labelUrl && (
                      <a href={labelUrl} target="_blank" rel="noreferrer">
                        <Button variant="outline" className="w-full">
                          Shipping label
                        </Button>
                      </a>
                    )}
                    {manifestUrl && (
                      <a href={manifestUrl} target="_blank" rel="noreferrer">
                        <Button variant="outline" className="w-full">
                          Manifest
                        </Button>
                      </a>
                    )}
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => docs.mutate()}
                    loading={docs.isPending}
                  >
                    Get label &amp; manifest
                  </Button>
                )}
                {liveShipment.status === 'PICKUP_SCHEDULED' && (
                  <p className="text-xs text-slate-500">
                    Stick the label on the parcel and keep it ready. The courier collects it at
                    pickup, and the order moves to shipped and delivered from their scans.
                  </p>
                )}
              </div>
            )}

            {BEFORE_PICKUP.includes(liveShipment.status) &&
              (cancelling ? (
                <div className="space-y-2">
                  <input
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Why cancel this shipment?"
                    className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm"
                  />
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setCancelling(false)}
                    >
                      Keep
                    </Button>
                    <Button
                      className="flex-1 bg-rose-600 hover:bg-rose-700"
                      disabled={cancelReason.trim().length < 3}
                      loading={cancel.isPending}
                      onClick={() => cancel.mutate()}
                    >
                      Cancel shipment
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="text-xs font-semibold text-slate-500 underline hover:text-slate-700"
                  onClick={() => setCancelling(true)}
                >
                  Cancel this shipment and create a new one
                </button>
              ))}
          </div>
        )}

        {subOrder.taxInvoice && (
          <Link
            to={`/orders/${subOrder.id}/invoice`}
            className="group flex items-center gap-3 rounded-xl bg-gradient-to-r from-[#a8161b] to-[#c2410c] px-4 py-3 text-white shadow-sm transition-shadow hover:shadow-md"
          >
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-white/15">
              <ReceiptText className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[11px] font-bold uppercase tracking-wider text-white/80">
                Tax invoice (bill)
              </span>
              <span className="block truncate font-mono text-[14px] font-black">
                {subOrder.taxInvoice.invoiceNumber}
              </span>
            </span>
            <span className="rounded-lg bg-white px-2.5 py-1 text-[12px] font-extrabold text-[#a8161b] transition-transform group-hover:translate-x-0.5">
              View / print →
            </span>
          </Link>
        )}
      </CardBody>
    </Card>
  );
}

export { InvoicePage } from './TaxInvoicePage';
